import { useState } from "react";
import {
  useListAccountCommunications,
  getListAccountCommunicationsQueryKey,
  type CommunicationLogEntry,
} from "@workspace/api-client-react";
import { MessageCircle, Mail, Smartphone, FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Empty,
  EmptyTitle,
  EmptyDescription,
} from "@/components/ui/empty";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

// ── Channel definitions ───────────────────────────────────────────────────────

const CHANNELS = [
  { value: "whatsapp", label: "WhatsApp", Icon: MessageCircle },
  { value: "email", label: "Email", Icon: Mail },
  { value: "sms", label: "SMS", Icon: Smartphone },
] as const;

type ChannelValue = (typeof CHANNELS)[number]["value"];

// ── Humanize helpers ──────────────────────────────────────────────────────────

function humanizeEventType(eventType: string): string {
  // e.g. "charge_receipt" → "Charge Receipt"
  return eventType
    .split(/[_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

// ── Status badge ──────────────────────────────────────────────────────────────

const STATUS_VARIANT: Record<string, "default" | "destructive" | "secondary"> =
  {
    delivered: "default",
    failed: "destructive",
    pending: "secondary",
  };

function statusVariant(
  status: string,
): "default" | "destructive" | "secondary" {
  return STATUS_VARIANT[status] ?? "secondary";
}

// ── LogRow ────────────────────────────────────────────────────────────────────

function LogRow({ row }: { row: CommunicationLogEntry }) {
  const variant = statusVariant(row.status);
  const hasPdf = Boolean(row.pdfUrl);
  const hasError = row.status === "failed" && row.errorDetail;

  const badge = (
    <Badge variant={variant} className="capitalize shrink-0">
      {row.status}
    </Badge>
  );

  return (
    <div className="flex flex-col gap-1 py-3 px-1">
      <div className="flex items-start gap-2 flex-wrap">
        {/* Timestamp */}
        <span className="text-xs text-muted-foreground shrink-0">
          {format(new Date(row.sentAt), "dd MMM yyyy, HH:mm")}
        </span>

        {/* Event type */}
        <span className="text-sm font-medium">
          {humanizeEventType(row.eventType)}
        </span>

        {/* Status badge — wrap in tooltip when there's error detail */}
        {hasError ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>{badge}</TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-xs">
                {row.errorDetail}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          badge
        )}
      </div>

      {/* Recipient */}
      <p className="text-xs text-muted-foreground">To: {row.recipient}</p>

      {/* Body preview */}
      {row.bodyPreview && (
        <p className="text-sm text-foreground/80 line-clamp-2">
          {row.bodyPreview}
        </p>
      )}

      {/* Open document link */}
      {hasPdf && (
        <a
          href={row.pdfUrl!}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-primary underline-offset-4 hover:underline w-fit"
        >
          <FileText className="size-3" />
          Open document
        </a>
      )}
    </div>
  );
}

// ── CommunicationTab ──────────────────────────────────────────────────────────

export function CommunicationTab({ accountId }: { accountId: number }) {
  const [channel, setChannel] = useState<ChannelValue>("whatsapp");
  const isSms = channel === "sms";

  const { data, isLoading } = useListAccountCommunications(
    accountId,
    { channel },
    {
      query: {
        queryKey: getListAccountCommunicationsQueryKey(accountId, { channel }),
        enabled: !isSms,
      },
    },
  );

  const items = data?.items ?? [];

  return (
    <div className="flex gap-4 min-h-[400px]">
      {/* ── Left sidebar ── */}
      <div className="w-40 shrink-0 flex flex-col gap-1 pt-1">
        {CHANNELS.map(({ value, label, Icon }) => (
          <button
            key={value}
            type="button"
            onClick={() => setChannel(value)}
            className={cn(
              "flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors text-left",
              channel === value
                ? "bg-muted font-medium text-foreground"
                : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
            )}
          >
            <Icon className="size-4 shrink-0" />
            <span>{label}</span>
            {/* Per-channel count badge for the active channel */}
            {channel === value && !isSms && data?.total != null && (
              <Badge variant="secondary" className="ml-auto text-xs px-1.5">
                {data.total}
              </Badge>
            )}
          </button>
        ))}
      </div>

      {/* ── Right log pane ── */}
      <div className="flex-1 min-w-0">
        {/* SMS empty state — no API call fires */}
        {isSms && (
          <Empty className="h-full border border-dashed">
            <EmptyTitle>SMS coming soon</EmptyTitle>
            <EmptyDescription>
              SMS notifications are not yet enabled.
            </EmptyDescription>
          </Empty>
        )}

        {/* Loading skeletons */}
        {!isSms && isLoading && (
          <div className="flex flex-col gap-3 pt-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex flex-col gap-2 px-1">
                <Skeleton className="h-3 w-32" />
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-3 w-full max-w-sm" />
              </div>
            ))}
          </div>
        )}

        {/* Empty state for whatsapp/email */}
        {!isSms && !isLoading && items.length === 0 && (
          <Empty className="h-full border border-dashed">
            <EmptyTitle>No messages yet</EmptyTitle>
            <EmptyDescription>
              Messages will appear here after the first send.
            </EmptyDescription>
          </Empty>
        )}

        {/* Log list — plain .map(), NOT the table hook (prior infinite-render freeze) */}
        {!isSms && !isLoading && items.length > 0 && (
          <ScrollArea className="h-[500px] pr-2">
            {items.map((row, idx) => (
              <div key={row.id}>
                <LogRow row={row} />
                {idx < items.length - 1 && <Separator />}
              </div>
            ))}
          </ScrollArea>
        )}
      </div>
    </div>
  );
}
